import Link from "next/link"
import { useRouter } from "next/router"
import useTranslation from "next-translate/useTranslation"
import { useSelector } from "react-redux"
import { Col, Row } from "reactstrap"

import { Routes } from "@basics/routes"
import BaseLayout from "@components/BaseLayout"
import { AppState } from "@redux/reducer"
import { selectIsAuthenticated } from "@redux/reducer/auth"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n/i18n"
import { useLoginRouteWithRedirectBack } from "@services/routesHelper"

import HtmlContent from "./common/HtmlContent"
import { withDynamicNamespaces } from "./hoc/withDynamicNamespaces"

const usedNamespaces: NamespaceShortcut[] = []

type AbstractIProps<GenericStatusCode extends StatusCode> = {
  /**
   * Error from the api request. Will be extended by error namespace and translated
   */
  error?: string
  /**
   * Params that should be used when translating an error i18n string to user readable text.
   */
  errorTranslationParams?: Record<string, string>
  /**
   * Error code, e.g. 500, 404 etc.
   */
  statusCode?: GenericStatusCode
  /**
   * Title of the page
   */
  title?: string
  /**
   * Extra data to make analysis more easy vor coders.
   */
  extraData?: string
}

/**
 * The props for status code 301 with a forwarding route.
 */
type IPropsForStatusCode301 = AbstractIProps<301> & {
  /**
   * The forwarding route for status code 301.
   */
  forwardingRouteFor301: string
}

/**
 * Checks if the props are for status code 301.
 */
const isPropsForStatusCode301 = (props: IProps<StatusCode>): props is IPropsForStatusCode301 =>
  props.statusCode === 301

/**
 * The props used for the component itself
 * ensure that if the status code 301 is specified, a forwarding route is also specified;
 * otherwise the forwarding route is not allowed.
 */
type IProps<GenericStatusCode extends StatusCode> = GenericStatusCode extends Exclude<StatusCode, 301>
  ? AbstractIProps<Exclude<StatusCode, 301>>
  : IPropsForStatusCode301

/**
 * Contains all current used status codes.
 */
export const StatusCodeList = [301, 400, 403, 404, 423, 500] as const

/**
 * The status codes are defined by the World Wide Web Consortium (W3C).
 * The application uses currently only a subset of the status codes.
 *
 * @see https://www.w3docs.com/learn-html/http-status-messages.html
 */
export type StatusCode = typeof StatusCodeList[number]

/**
 * This component provides an error message to the user
 */
const ErrorPage = <GenericStatusCode extends StatusCode, GenericProps extends IProps<GenericStatusCode>,>(props: GenericProps): JSX.Element => {
  const { error, errorTranslationParams, extraData } = props
  const { t } = useTranslation("common")
  const td = useDynamicTranslation()
  const router = useRouter()

  // When the status code is directly destructed from the props, the following error occurs in tests:
  //  TS7022: `'statusCode' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.`
  // Note: This error occured after the status code was made generic.
  // This problem can be avoided by defining the status code and its default value, as well as the dependent title property, separately.
  const statusCode = props.statusCode ?? 500
  const title = props.title ?? `error:title.${statusCode}`

  const errorString = error ? td("error", error, errorTranslationParams) : td("error", `explanation.${statusCode}`)

  const isAuthenticated = useSelector((state: AppState) => selectIsAuthenticated(state))
  const loginWithRedirectBack = useLoginRouteWithRedirectBack()

  return <BaseLayout pageTitle={t(title)}>
    <Row>
      <Col className="text-center">
        <h1>{t(title)}</h1>
        <p>
          <HtmlContent content={errorString} />
        </p>
        {extraData && <p>{extraData}</p>}
        <div className="btn-flex-layout justify-content-center">
          {
            /*
            * To avoid to crash the browser history
            * and produce the problem of a non-functional back button of the browser
            * the user should click onto a link to be forwarded to his target,
            * instead of being pushed forward automatically.
            */
          }
          {isPropsForStatusCode301(props)
            ? <Link
              href={props.forwardingRouteFor301}
              className="btn primary-btn"
              title={td("goto", "forwarding")}
            >
              {td("goto", "forwarding")}
            </Link>
            : <>
              <Link
                href='#'
                className="btn primary-btn"
                title={t("goto.reload")}
                onClick={router.reload}
              >
                {t("goto.reload")}
              </Link>
              {isAuthenticated
                ? <Link
                  href={Routes.MyProjects}
                  className="btn primary-btn"
                  title={td("goto", "user.myProjects")}
                >
                  {td("goto", "user.myProjects")}
                </Link>
                : <Link
                  href={loginWithRedirectBack}
                  className="btn primary-btn"
                  title={t("goto.login")}
                >
                  {t("goto.login")}
                </Link>
              }
              <Link
                href={Routes.Home}
                className="btn primary-btn"
                title={t("goto.home")}
              >
                {t("goto.home")}
              </Link>
            </>}
        </div>
      </Col>
    </Row>
  </BaseLayout>
}

export default withDynamicNamespaces<IProps<StatusCode>>(ErrorPage<StatusCode, IProps<StatusCode>>, usedNamespaces)
